<template>
  <div class="checklist-modal">
    <header class="checklist-header">
      <Title size="5">
        Save a Checklist template
      </Title>

      <IconButton
        class="checklist-close"
        icon="fas fa-times-circle"
        @click="emit('close')"
      />
    </header>

    <div class="replace-title">
      <Label size="4">Replace an existing Checklist template</Label>
    </div>

    <div class="checklist-scroller">
      <div class="checklist-scroller-body">
        <LoaderWithText v-if="isFetching" />

        <Stack
          v-else-if="availableChecklistsForEntity.length"
          gap="2"
        >
          <article
            v-for="checklist in availableChecklistsForEntity"
            :key="checklist.id"
            class="item"
            :class="{
              'is-selected': chosenId === checklist.id,
            }"
            @click="handleChecklistChoice(checklist.id)"
          >
            <div class="item-header">
              <input
                type="radio"
                :checked="chosenId === checklist.id"
              >
              <Title
                class="item-header-name"
                size="4_5"
              >
                {{ checklist.name }}
              </Title>
              <Icon
                class="item-icon"
                icon="fas fa-check-circle"
              />
            </div>

            <p>
              {{ checklist.section_count }} sections —
              {{ checklist.item_count }} documents
            </p>
          </article>
        </Stack>

        <div v-else>
          No checklist found
        </div>
      </div>
    </div>

    <footer class="checklist-footer">
      <Stack gap="2">
        <Label size="4">Or create a new Checklist template:</Label>
        <TextInput
          v-model="newName"
          @input="handleNameChange"
        />
      </Stack>

      <Button
        class="checklist-footer-button"
        :variant="chosenId ? 'danger' : 'success'"
        icon-before="fas fa-save"
        spacing="loose"
        :loading="isSaving"
        :disabled="!canSave"
        @click="saveChecklistTemplate"
      >
        {{ chosenName ? `Replace ${chosenName}` : `Save as new template` }}
      </Button>
    </footer>
  </div>
</template>

<script setup>
import { computed, defineEmits, onMounted, ref } from 'vue';
import { ToastProgrammatic as Toast } from 'buefy';
import {
  Button,
  Icon,
  IconButton,
  Label,
  LoaderWithText,
  Stack,
  TextInput,
  Title,
} from '@stakeui';

const props = defineProps({
  currentChecklist: {
    type: Object,
    required: true,
  },
  entityExternalId: {
    type: String,
    default: '',
  },
  hasChecklist: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const csrfToken = ref('');
const chosenId = ref('');
const newName = ref('');
const isFetching = ref(true);
const isSaving = ref(false);
const availableChecklistsForEntity = ref([]);

const loadAvailableChecklists = async () => {
  if (availableChecklistsForEntity.value.length > 0) return;

  isFetching.value = true;

  try {
    const response = await fetch(
        `/api/v1/client-portal/checklists/available/${props.entityExternalId}/`,
    );

    if (!response.ok) {
      throw new Error(
          `Failed to fetch available checklists: ${response.status}`,
      );
    }

    const data = await response.json();
    availableChecklistsForEntity.value = data.checklists || [];
  } catch (err) {
    console.error('Error loading available checklists:', err);
    availableChecklistsForEntity.value = [];
  } finally {
    isFetching.value = false;
  }
};

const transformIntoDefinition = sections => {
  const output = {};

  for (const [key, value] of Object.entries(sections)) {
    const notes = value.display_name || key;
    const items = value.items || [];
    const document_type_pks = items.map(item => item.document_type_id);

    output[key] = {
      notes,
      document_type_pks,
    };
  }

  return output;
};

const saveChecklistTemplate = async () => {
  // We're either updating an existing checklist or creating a new one
  const updating = !!chosenId.value;

  if (updating && !window.confirm('This will replace your current template. Are you sure you want to proceed?')) {
    return;
  }

  isSaving.value = true;

  const url = updating ? `/api/v1/checklists/${chosenId.value}/` : '/api/v1/checklists/create/';
  const method = updating ? 'PUT' : 'POST';
  const definition = transformIntoDefinition(props.currentChecklist.sections); // Same with updating and creating
  const body = updating ? { definition } : {
    name: newName.value,
    definition,
    template_id: 1,
  };

  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken.value,
      },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Failed to save changes: ${response.status}`);
    }

    Toast.open({
      message: `Template successfully ${updating ? 'updated' : 'saved'}.`,
      type: 'is-success',
    });

    emit('close');
  } catch (err) {
    console.error('Error saving template:', err);
  } finally {
    isSaving.value = false;
  }
};

const handleChecklistChoice = checklistId => {
  if (newName.value) {
    newName.value = '';
  }

  chosenId.value === checklistId ?
    (chosenId.value = '') :
    (chosenId.value = checklistId);
};

const handleNameChange = () => {
  if (chosenId.value) {
    chosenId.value = '';
  }
};

const chosenName = computed(() => {
  if (!chosenId.value) return '';
  const match = availableChecklistsForEntity.value.find(el => el.id === chosenId.value);
  return match ? match.name : '';
});
const canSave = computed(() => chosenId.value || newName.value.trim());

onMounted(async () => {
  csrfToken.value = document.querySelector(
      '[name="csrfmiddlewaretoken"]',
  )?.value;
  await loadAvailableChecklists();
});
</script>

<style scoped>
.checklist-modal {
  --spacing: 1.5em;
  max-width: 100%;
  width: 32em;
}

.checklist-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing) var(--spacing) 0;
}

.checklist-close {
  color: var(--stk-text-weak);
  font-size: 1.25em;
  transition-duration: var(--stk-transition-duration);
  transition-property: color;

  &:hover {
    color: var(--stk-text-normal);
  }
}

.replace-title {
  padding: calc(0.25 * var(--spacing)) var(--spacing) var(--spacing);;
}

.checklist-scroller {
  background-color: var(--stk-text-95);
  border-color: var(--stk-border);
  border-style: solid;
  border-width: 1px 0;
  box-shadow: inset 0 0.25em 0.5em rgb(0 0 0 / 5%);
  height: 20rem;
  overflow-y: scroll;
}

.checklist-scroller-body {
  padding: var(--spacing);
}

.item {
  background-color: #fff;
  border-radius: 0.75em;
  border: 1px solid var(--stk-border);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  position: relative;
  padding: 1.125em 1.25em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, color, border-color, box-shadow;

  &:hover {
    border-color: var(--stk-primary);
  }

  &.is-selected {
    border-color: var(--stk-primary);
    box-shadow: 0 0 0 1px var(--stk-primary),
      0 4px 4px 0 hsl(var(--stk-primary-hsl) / 20%);

    .item-icon {
      opacity: 1;
    }
  }
}

.item-header {
  display: flex;
  align-items: center;
  gap: 0.5em;
}

.item-header-name {
  flex-grow: 1;
}

.item-icon {
  color: var(--stk-primary);
  opacity: 0;
}

.checklist-footer {
  display: flex;
  flex-direction: column;
  gap: 1em;
  justify-content: stretch;
  padding: 1.5em;
}

.checklist-footer-button {
  flex-grow: 1;
}
</style>
